/**
 * What each measure means, in a sentence a reader can act on — the hover text Rand
 * asked for on 2026-09-23 so nobody has to ask what a tile is counting.
 *
 * Keyed by the measure's key where it has one (METRICS, SCORE_RULES, the team rows),
 * so a label can change without its definition going missing. `more` names the entry
 * on the Data & method page that gives the full, technical answer; the tooltip links
 * to it. Keep both in step with References/MEASURE-RULES.md: when a rule changes, the
 * sentence here and the entry there change with it.
 */
import { COLLECTION_LABEL } from './collection'

export interface Definition {
  /** The measure's name, as the tooltip's heading. */
  label: string
  /** One or two plain sentences. */
  text: string
  /** Anchor id of the fuller entry on /method, when there is more worth saying. */
  more?: MethodAnchor
  /** The card also names the bill months the selected range reads (collection rate). */
  window?: boolean
}

/** The Data & method entries a tooltip can link to. See pages/DataMethod.tsx. */
export type MethodAnchor =
  | 'cost-per-claim'
  | 'payroll-labor'
  | 'person-pay'
  | 'billed-claims'
  | 'unattributed-labor'
  | 'no-claim-why'
  | 'collection-rate'
  | 'billed-vs-resolved'
  | 'touch-shares'
  | 'open-worked'
  | 'open-recency'
  | 'facilities'
  | 'team-figures'
  | 'score'
  | 'peer-group'
  | 'costs-above-peer'
  | 'management'
  | 'filters'
  | 'weighted-claims'

const COST_PER_CLAIM: Definition = {
  label: 'Cost per claim',
  text: 'Payroll labor ÷ billed claims. Everyone’s pay counts, including people who billed nothing, so this is what a claim really costs.',
  more: 'cost-per-claim',
}

const COLLECTION: Definition = {
  label: COLLECTION_LABEL,
  text: 'Charge on resolved claims ÷ total charge, for claims billed over six months ending three months before the range — so bills have had time to resolve. A longer range adds its extra months to the window. Each month in a grid is the six months ending three months before it.',
  more: 'collection-rate',
  window: true,
}

const BILLED_VS_RESOLVED: Definition = {
  label: 'Claims billed vs resolved %',
  text: 'Claims resolved ÷ claims billed in the same period. Not capped: clearing a backlog takes it past 100%.',
  more: 'billed-vs-resolved',
}

const ZERO_TOUCH: Definition = {
  label: '0% touch share',
  text: 'Of the claims resolved in the month (whenever billed), the share that closed with no touch at all. Higher is better.',
  more: 'touch-shares',
}

const OPEN_WORKED: Definition = {
  label: 'Open claims worked in month',
  text: 'Of the claims open on a worklist at each month end, the share touched that month. System worklists are left out.',
  more: 'open-worked',
}

const DEFINITIONS: Record<string, Definition> = {
  costPerClaim: COST_PER_CLAIM,
  facilityCostPerClaim: {
    label: 'Cost per claim',
    text: 'Claim labor filed under the facility ÷ its billed claims. Payroll has no facility, so this is the labor allocated to the facility’s claims (and labor on no claim filed under it) — what the Overview’s facility bars divide.',
    more: 'cost-per-claim',
  },
  facilityBilledVsResolved: {
    label: 'Claims billed vs resolved %',
    text: 'Claims resolved in the months (whenever billed) ÷ claims billed in them, at this facility. Not capped: clearing a backlog takes it past 100%.',
    more: 'billed-vs-resolved',
  },
  teamCostPerClaim: {
    label: 'Team cost per claim',
    text: 'The team’s payroll labor ÷ the team’s billed claims, over everyone on it — people with no claims bring their full pay.',
    more: 'team-figures',
  },
  payrollLabor: {
    label: 'Payroll labor',
    text: 'Benefit-loaded pay for everyone in scope, whether or not they billed a claim. The top half of cost per claim.',
    more: 'payroll-labor',
  },
  // The person's own pay and hours. Shown, never ranked (2026-09-23).
  personPayroll: {
    label: 'Payroll labor',
    text: 'This person’s pay from payroll, bonus included and benefit load left out. Not the figure cost per claim divides, which carries the benefit load. Never ranked.',
    more: 'person-pay',
  },
  hoursWorked: {
    label: 'Total worked',
    text: 'Every paid hour, overtime and PTO included, from payroll. Never ranked.',
    more: 'person-pay',
  },
  otHours: {
    label: 'OT',
    text: 'Overtime hours — already inside total worked, not on top of it. Only Paylocity payroll records overtime.',
    more: 'person-pay',
  },
  ptoHours: {
    label: 'PTO',
    text: 'Paid time off, in hours — already inside total worked, not on top of it. Only Paylocity payroll records PTO.',
    more: 'person-pay',
  },
  claims: {
    label: 'Billed claims',
    text: 'Claims billed in the period — the bottom half of cost per claim.',
    more: 'billed-claims',
  },
  billedClaims: {
    label: 'Billed claims',
    text: 'Claims billed in the period — the bottom half of cost per claim.',
    more: 'billed-claims',
  },
  billedPerMonth: {
    label: 'Billed claims per month',
    text: 'Billed claims ÷ months in the range.',
    more: 'billed-claims',
  },
  perEmployeePerMonth: {
    label: 'Billed claims / employee / month',
    text: 'Billed claims ÷ months in the range, averaged over the people on the team who carry claims.',
    more: 'team-figures',
  },
  collectedPct: COLLECTION,
  collection: COLLECTION,
  closedClaimPct: BILLED_VS_RESOLVED,
  closedPct: BILLED_VS_RESOLVED,
  zeroTouchShare: ZERO_TOUCH,
  zeroTouch: ZERO_TOUCH,
  touchesPerClaim: {
    label: 'Touches per touched claim',
    text: 'Touches ÷ claims touched at least once, over the claims resolved in the month (whenever billed). Lower means less rework per claim.',
    more: 'touch-shares',
  },
  openWorkedPct: OPEN_WORKED,
  openWorked: { ...OPEN_WORKED, text: `${OPEN_WORKED.text} Pooled over the team’s pile.` },
  facilities: {
    label: 'Facilities handled',
    text: 'Distinct facilities billed for. Facilities that resolve to UNKNOWN are not counted.',
    more: 'facilities',
  },
  facilitiesPerReport: {
    label: 'Facilities handled per person',
    text: 'Distinct facilities billed for, per person carrying claims, averaged across the team.',
    more: 'facilities',
  },
  openAtMonthEnd: {
    label: 'Open at month end',
    text: 'Claims open on the person’s own worklist at each month end — the pile the worked % above is out of. System worklists are left out. The running month is the day the worklist was last loaded.',
    more: 'open-worked',
  },
  openClaims: {
    label: 'Open claims',
    text: 'Claims open on the person’s own worklist at the latest month end. System worklists are left out.',
    more: 'open-worked',
  },
  notWorkedThisMonth: {
    label: 'Not worked this month',
    text: 'Open claims at the latest month end that this person did not touch in that month. A claim worked in an earlier month and not since counts here, so it is not the same as never worked.',
    more: 'open-worked',
  },
  workedThisMonth: {
    label: 'Worked this month',
    text: 'Open claims at the latest month end that their owner touched in that month. With Not worked this month, it adds up to the open pile.',
    more: 'open-worked',
  },
  neverWorked: {
    label: 'Never worked',
    text: 'Open claims nobody has touched at all since touch records begin. A touch by anyone counts as worked, not only by the claim’s owner.',
    more: 'open-recency',
  },
  workedLast30: {
    label: 'Worked in last 30 days',
    text: 'Open claims whose most recent touch, by anyone, was in the 30 days up to the worklist date. Its share is the percentage of the pile worked within 30 days.',
    more: 'open-recency',
  },
  workedOver30: {
    label: 'Last worked over 30 days ago',
    text: 'Open claims touched at some point, but not in the 30 days up to the worklist date.',
    more: 'open-recency',
  },
  facilityClaims: {
    label: 'Claims by facility',
    text: 'Claims allocated to this person at each facility, from the claim data. It keeps months the person was not paid in, so it can run a little above their billed claims.',
    more: 'facilities',
  },
  workers: {
    label: 'People carrying claims',
    text: 'People on the team who billed at least one claim in the range.',
    more: 'team-figures',
  },
  management: {
    label: 'Include management',
    text: 'Whether supervisors, managers, directors and VPs — the payroll management flag — are in the figures. The Overview counts them; the people tabs leave them out, so covering a few claims does not skew a supervisor’s own numbers or their team’s.',
    more: 'management',
  },
  claimBasis: {
    label: 'Weighted claims',
    text: 'Off: every billed claim counts 1. On: claims count by the claim rules — a coinsurance payer’s claims at its weight (or not at all for a biller it is excluded from), a hard payer’s at its weight (1 unless set). Cost per claim, claims per month, scores and ranks follow; claims billed vs resolved % does not.',
    more: 'weighted-claims',
  },
  score: {
    label: 'Score',
    text: 'Points for each measure where the person beats their peer average, minus 3 when claims billed vs resolved is under 50%. From −3 to +11, in half points. Scored on the selected months’ figures pooled together — not an average of monthly scores.',
    more: 'score',
  },
  penalty: {
    label: 'Penalty',
    text: '−3 when claims billed vs resolved is under 50% — the one line that takes points away.',
    more: 'score',
  },
  peers: {
    label: 'Peers',
    text: 'The people this person is scored against: same department and job title, and same shore for cost per claim. Fewer than five means no rank.',
    more: 'peer-group',
  },
  rank: {
    label: 'Rank',
    text: 'Place by score among everyone active in the same job title. Anyone in the title with no score sits at the bottom as “no data”. Fewer than five with a score means no rank. Hover a rank for who it is against.',
    more: 'peer-group',
  },
  vsPeer: {
    label: '$ vs peer average',
    text: '(Their cost per claim − their peer group’s average) × their billed claims: what the gap costs. Positive is dearer than peers.',
    more: 'costs-above-peer',
  },
  resolvedClaims: {
    label: 'Resolved claims',
    text: 'Claims resolved in the period. Counted by resolve date, not bill date, so it is not the cost-per-claim denominator.',
    more: 'billed-vs-resolved',
  },
}

/**
 * What the claim and payer dimensions are, for the hover beside a filter or a column
 * (Asana 1218824501473012: "clarify Payer Type / Payer Name / Claim Type / Service
 * Type"). The four were read as one another — a claim type for a payer type, the payer
 * for its type. From References/MEASURE-RULES.md (§7a, Rule 10.4a) and MODEL-SOURCES.
 */
export const DIMENSION_HELP = {
  claimType: {
    label: 'Claim type',
    text: 'Where the claim sits in the visit’s billing order — Primary, Secondary, Tertiary, Co-Insurance and so on (DimClaimType.ClaimType). A property of the claim, not of who pays it.',
  },
  serviceType: {
    label: 'Service type',
    text: 'The service line the claim bills — Inhouse, Ancillary or Other (DimClaimType.ServiceTypeName). Read off the claim, not a payroll cost centre.',
  },
  payerType: {
    label: 'Payer type',
    text: 'The payer’s category — Medicaid, Medicare, HMO and so on (DimPayor.Payor_Type). NCS income records with no type read as Income/Budget.',
  },
  payerName: {
    label: 'Payer name',
    text: 'The one payer billed on the claim (Payor_Name), such as a single HMO plan. Payer type groups payer names.',
  },
  facilityGroup: {
    label: 'Facility group',
    text: 'The group the claim’s facility is filed under. Exact on the Overview’s bars, Facilities, Claim detail, Touch detail and Worklist assignments; elsewhere each person counts by their share of claims and labor in the groups (M).',
  },
} as const satisfies Record<string, Definition>

/** The Employee control on the pages where it is set per page (EMPLOYEE_PAGE_SCOPED). */
export const EMPLOYEE_THIS_PAGE: Definition = {
  label: 'Employee, this page',
  text: 'On Supervisors and the Scorecard, Employee applies only when picked here. One picked on another page shows as “not applied”, with Apply here.',
  more: 'filters',
}

/** A definition with one more sentence about how this particular table uses it. */
export const withNote = (def: Definition | undefined, note: string): Definition | undefined =>
  def ? { ...def, text: `${def.text} ${note}` } : undefined

/** The definition for a key, or undefined so a caller can render nothing. */
export const define = (key: string | undefined): Definition | undefined =>
  key ? DEFINITIONS[key] : undefined

/**
 * A team measure's definition. Team rows reuse the person keys except cost per claim,
 * which for a team is a rollup over everyone on it.
 */
export const defineTeam = (key: string): Definition | undefined =>
  define(key === 'costPerClaim' ? 'teamCostPerClaim' : key)
